import {
  ThemeProvider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Card,
  Typography,
  Dialog,
  DialogContent,
  Grow,
  IconButton,
  Link,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { ItemAPI } from '../../api/item';
import {
  formatDateTime,
  groupBy,
  printRoundedNumber,
  dateFormats,
} from '../../shared/utils';
import { ItemSearchInput } from '../item/item-search-input';
import { LabeledInput } from '../shared/labeled-input';
import { useNotificationContext } from '../shared/notification';
import { SmallCheckBox } from '../shared/small-check-box';
import {
  CardContainer,
  colors,
  defaultTheme,
  GridContainer,
  GridItem,
  LabeledText,
  CompactTableCell,
  CompactTableRow,
} from '../shared/ui-extension';
import {
  transactionTypeDescriptions,
  inboundOrderTypes,
  transactionTypeShortDescriptions,
  transactionTypes,
} from './constants';
import {
  getDefaultInventoryImpactDialog,
  InventoryImpactDialog,
} from './inventory-impact-dialog';
import { TransactionDetails } from './transaction-details';
import { TransactionStatus } from './transaction-status';
import { OpenInNewOutlined } from '@mui/icons-material';
import { differenceInHours } from 'date-fns';
import Head from 'next/head';

const transactionTypeColors = {
  currentStock: '#ddedce',
  purchaseOrder: '#cee9ed',
  transferIn: '#e2cfe8',
  futureStock: '#ede1ce',
};

export const InventoryManagement = ({
  itemId,
  locationId,
  minColWidth = 130,
  isInputDisabled = false,
  highlightOrderNumber,
}) => {
  const {
    handleException,
    openDialog,
    queryParameters,
    updateQueryParameters,
    useExceptionHandler,
  } = useNotificationContext();

  const [inventoryTransactions, setInventoryTransactions] = useState([]);
  const [selectedItem, setSelectedItem] = useState({});
  const [selectedTransaction, setSelectedTransaction] = useState({});
  const [isPageInputDisabled, setIsPageInputDisabled] =
    useState(isInputDisabled);
  const [inventoryImpactDialog, setInventoryImpactDialog] = useState(
    getDefaultInventoryImpactDialog()
  );

  const isUsedAsComponent = !!itemId;

  // Query strings are always passed in as lower case from P21 ribbon metrics.
  useEffect(() => {
    if (isUsedAsComponent) return;

    if (queryParameters.itemid) {
      updateQueryParameters({
        itemid: undefined,
        itemId: queryParameters.itemid,
      });
    }

    if (queryParameters.locationid) {
      updateQueryParameters({
        locationid: undefined,
        locationId: queryParameters.locationid,
      });
    }
  }, [queryParameters.itemid, queryParameters.locationid]);

  useEffect(() => {
    const selectedItemId = queryParameters.itemId || itemId;

    if (selectedItemId) {
      setSelectedItem({ item_id: selectedItemId.toUpperCase() });
    }
  }, [queryParameters.itemId, itemId]);

  // Allow users to refresh the page without re-selecting the item.
  useEffect(() => {
    if (!isUsedAsComponent && selectedItem.item_id) {
      updateQueryParameters({ itemId: selectedItem.item_id });
    }
  }, [selectedItem.item_id]);

  useEffect(() => {
    if (selectedItem.item_id) {
      loadInventoryTransactions();
    } else {
      setInventoryTransactions([]);
    }
  }, [selectedItem.item_id, queryParameters.locationId, locationId]);

  const loadInventoryTransactions = useExceptionHandler(async () => {
    const selectedLocationId = queryParameters.locationId || locationId;
    const { data } = await ItemAPI.getInventoryTransactions({
      itemId: selectedItem.item_id,
      locationId: selectedLocationId,
    });
    setInventoryTransactions(data);
  });

  const updateInventoryTransaction = async (transactionToUpdate) => {
    setIsPageInputDisabled(true);

    try {
      const { data: newInventoryTransactions } =
        await ItemAPI.updateInventoryTransaction(transactionToUpdate);
      setInventoryTransactions((prev) => {
        const newTransactions = [
          ...prev.filter(
            (t) =>
              !newInventoryTransactions.some(
                (nt) => nt.locationId === t.locationId
              )
          ),
          ...newInventoryTransactions,
        ];
        return newTransactions;
      });
    } catch (e) {
      handleException(e);
    }

    setIsPageInputDisabled(false);
  };

  const groupedTransactions =
    inventoryTransactions &&
    groupBy(inventoryTransactions, 'locationName').sort(
      (a, b) => a.values[0]?.locationId - b.values[0]?.locationId,
      {}
    );

  return (
    <ThemeProvider theme={defaultTheme}>
      <Head>
        <title>
          PAM: IM{selectedItem.item_id ? ` - ${selectedItem.item_id}` : ''}
        </title>
      </Head>
      <GridContainer rowSpacing={3}>
        <GridItem xs={12}>
          {isUsedAsComponent && isPageInputDisabled ? (
            <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
              {selectedItem.item_id}
            </Typography>
          ) : (
            <ItemSearchInput
              defaultItemId={selectedItem.item_id}
              disabled={isPageInputDisabled}
              onItemSelected={(selectedItem) => {
                setSelectedItem(selectedItem || {});
              }}
            />
          )}
        </GridItem>
        <GridItem xs={12}>
          <GridContainer>
            {groupedTransactions?.map(
              ({ key: locationName, values: transactions }, i) => (
                <GridItem xs={12} key={i}>
                  <CardContainer noPadding>
                    <GridContainer>
                      <GridItem xs={12}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
                          {locationName}
                        </Typography>
                      </GridItem>
                      {transactions?.map((t, ti) => {
                        const isTbd =
                          t.transactionType ===
                          transactionTypes.unallocatedStock;

                        const isCurrentStock =
                          t.transactionType === transactionTypes.currentStock;

                        const isFutureStock =
                          t.transactionType == transactionTypes.futureStock;

                        const availableFullCaseQty =
                          t.unallocatedQuantity -
                          (t.unallocatedQuantity % t.caseQuantity || 0);

                        const availablePartialQty =
                          t.unallocatedQuantity -
                          availableFullCaseQty +
                          (t.allocated?.find(
                            (a) =>
                              a.transactionType ===
                              transactionTypes.partialReserve
                          )?.allocatedQuantity || 0);

                        const ghostStockQuantity = t.allocated?.find(
                          (a) =>
                            a.transactionType === transactionTypes.ghostStock
                        )?.allocatedQuantity;

                        t.allocated = t.allocated?.filter(
                          (a) =>
                            ![
                              transactionTypes.ghostStock,
                              transactionTypes.partialReserve,
                            ].includes(a.transactionType)
                        );

                        return (
                          <GridItem xs={12} key={ti}>
                            <CardContainer
                              backgroundColor={
                                transactionTypeColors[t.transactionType] ||
                                colors.lightGray
                              }
                              noPadding
                            >
                              <GridContainer>
                                <GridItem xs={12}>
                                  <GridContainer justifyContent='space-between'>
                                    <GridItem xs={11}>
                                      <GridContainer columns={18} spacing={2}>
                                        {t.unallocatedQuantity !== undefined &&
                                        !isFutureStock ? (
                                          <GridItem
                                            xs={1.2}
                                            title={`Total Quantity Allocated: ${printRoundedNumber(
                                              t.quantity -
                                                t.unallocatedQuantity,
                                              0
                                            )}`}
                                          >
                                            <LabeledText
                                              label='Avail Qty'
                                              sx={
                                                (!t.unallocatedQuantity && {
                                                  color: colors.warningHigh,
                                                  fontWeight: 'bold',
                                                }) ||
                                                (t.unallocatedQuantity && {
                                                  color: colors.white,
                                                  backgroundColor:
                                                    t.transactionType ===
                                                    transactionTypes.currentStock
                                                      ? colors.highlightGreen
                                                      : colors.highlightDarkCyan,
                                                  borderRadius: 1,
                                                  paddingLeft: 0.75,
                                                  fontWeight: 'bold',
                                                })
                                              }
                                              text={printRoundedNumber(
                                                t.unallocatedQuantity,
                                                0
                                              )}
                                            />
                                          </GridItem>
                                        ) : (
                                          false
                                        )}
                                        {t.quantity ? (
                                          <GridItem xs={1}>
                                            <LabeledText
                                              label={'Total Qty'}
                                              title={
                                                ghostStockQuantity
                                                  ? printRoundedNumber(
                                                      -ghostStockQuantity,
                                                      0
                                                    )
                                                  : ''
                                              }
                                              text={printRoundedNumber(
                                                t.quantity,
                                                0
                                              )}
                                            />
                                          </GridItem>
                                        ) : (
                                          false
                                        )}
                                        {isCurrentStock ? (
                                          <>
                                            <GridItem>
                                              <LabeledText
                                                label={'Avail Full Cases'}
                                                title={
                                                  availableFullCaseQty >= 0 &&
                                                  t.caseQuantity > 0
                                                    ? `Number of cases available: ${printRoundedNumber(
                                                        availableFullCaseQty /
                                                          t.caseQuantity,
                                                        0
                                                      )} (${printRoundedNumber(
                                                        t.caseQuantity,
                                                        0
                                                      )}/cs)`
                                                    : ''
                                                }
                                                sx={{
                                                  fontWeight: 'bold',
                                                  ...(availableFullCaseQty <= 0
                                                    ? {
                                                        color:
                                                          colors.warningHigh,
                                                      }
                                                    : null),
                                                }}
                                                text={printRoundedNumber(
                                                  availableFullCaseQty,
                                                  0
                                                )}
                                              />
                                            </GridItem>
                                            <GridItem>
                                              <LabeledText
                                                label={'Avail Partials'}
                                                title={`Pieces available in the partial picking area.`}
                                                sx={{
                                                  fontWeight: 'bold',
                                                  ...(availablePartialQty <= 0
                                                    ? {
                                                        color:
                                                          colors.warningHigh,
                                                      }
                                                    : null),
                                                }}
                                                text={printRoundedNumber(
                                                  availablePartialQty,
                                                  0
                                                )}
                                              />
                                            </GridItem>
                                          </>
                                        ) : null}
                                        {!isFutureStock ? (
                                          <GridItem>
                                            <LabeledText
                                              label={
                                                transactionTypeDescriptions[
                                                  t.transactionType
                                                ] || t.transactionType
                                              }
                                              text={
                                                (inboundOrderTypes.includes(
                                                  t.transactionType
                                                ) && (
                                                  <Link
                                                    onClick={() => {
                                                      setSelectedTransaction(t);
                                                    }}
                                                  >
                                                    {t.transactionReferenceId}
                                                  </Link>
                                                )) ||
                                                t.transactionReferenceNumber
                                              }
                                            />
                                          </GridItem>
                                        ) : (
                                          false
                                        )}
                                        <GridItem>
                                          {isFutureStock ? (
                                            <LabeledText
                                              label='Estimated Lead Time'
                                              text={
                                                Math.ceil(
                                                  differenceInHours(
                                                    new Date(
                                                      t.transactionRequiredAt
                                                    ),
                                                    new Date()
                                                  ) / 24
                                                ) + ' days'
                                              }
                                            />
                                          ) : (
                                            t.transactionCreatedAt && (
                                              <LabeledText
                                                label='Order Date'
                                                text={formatDateTime(
                                                  t.transactionCreatedAt,
                                                  dateFormats.short
                                                )}
                                              />
                                            )
                                          )}
                                        </GridItem>
                                        {t.supplierName && (
                                          <GridItem
                                            title={
                                              t.supplierId &&
                                              `Supplier ID: ${t.supplierId}`
                                            }
                                          >
                                            <LabeledText
                                              label='Supplier'
                                              text={t.supplierName}
                                            />
                                          </GridItem>
                                        )}
                                        {t.buyerName && (
                                          <GridItem>
                                            <LabeledText
                                              label='Buyer'
                                              text={t.buyerName}
                                            />
                                          </GridItem>
                                        )}
                                        {t.linkedTransactionReferenceId && (
                                          <GridItem>
                                            <LabeledText
                                              label='Created for'
                                              text={
                                                t.linkedTransactionReferenceId
                                              }
                                            />
                                          </GridItem>
                                        )}
                                        {t.containerName && (
                                          <GridItem>
                                            <LabeledText
                                              label='Truck/Vessel'
                                              text={`${t.containerName} / ${t.vesselName}`}
                                            />
                                          </GridItem>
                                        )}
                                      </GridContainer>
                                    </GridItem>
                                    {t.transactionOriginalRequiredAt && (
                                      <GridItem>
                                        <LabeledText
                                          label={(() => {
                                            if (t.containerExpectedArrivalDate)
                                              return 'Truck Arrival Date';
                                            if (isFutureStock) return '';
                                            return 'Available Date';
                                          })()}
                                          text={formatDateTime(
                                            t.containerExpectedArrivalDate ||
                                              t.transactionOriginalRequiredAt,
                                            dateFormats.short
                                          )}
                                          sx={{
                                            ...{ fontWeight: 'bold' },
                                            ...(t.transactionOriginalRequiredAt !==
                                              t.transactionRequiredAt && {
                                              color: colors.warningHigh,
                                            }),
                                          }}
                                        />
                                      </GridItem>
                                    )}
                                  </GridContainer>
                                </GridItem>
                                {t.allocated && (
                                  <GridItem xs={12}>
                                    <Card sx={{ padding: 0 }}>
                                      <TableContainer>
                                        <Table size='small'>
                                          <TableHead
                                            sx={{
                                              backgroundColor: colors.lightGray,
                                            }}
                                          >
                                            <TableRow>
                                              <CompactTableCell
                                                width={minColWidth * 0.6}
                                              >
                                                {isTbd
                                                  ? 'Unallocated'
                                                  : 'Allocated'}
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth * 0.6}
                                              >
                                                Ordered
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth}
                                              >
                                                Type
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth}
                                              >
                                                Order/Line #
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth}
                                              >
                                                Order Date
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth}
                                              >
                                                Required Date
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth * 1}
                                              >
                                                Order Status
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth * 2}
                                              >
                                                Account
                                              </CompactTableCell>
                                              <CompactTableCell
                                                width={minColWidth * 1.25}
                                              >
                                                Consultant
                                              </CompactTableCell>
                                              <CompactTableCell>
                                                Linked PO/Transfer
                                              </CompactTableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {t.allocated.map((a, ai) => {
                                              const findTransaction = (
                                                inventoryTransactions
                                              ) => {
                                                return inventoryTransactions
                                                  .filter(
                                                    (inboundTran) =>
                                                      inboundTran.transactionId ===
                                                      t.transactionId
                                                  )[0]
                                                  ?.allocated.filter(
                                                    (outboundTran) =>
                                                      outboundTran.transactionId ===
                                                      a.transactionId
                                                  )[0];
                                              };

                                              const updateAllocatedTransaction =
                                                (fields) => {
                                                  const transactionToUpdate =
                                                    findTransaction(
                                                      inventoryTransactions
                                                    );

                                                  if (transactionToUpdate) {
                                                    const pendingTransactions =
                                                      [
                                                        {
                                                          ...transactionToUpdate,
                                                          ...fields,
                                                          itemUid: t.itemUid,
                                                          locationId:
                                                            t.locationId,
                                                          isInbound: false,
                                                        },
                                                      ];

                                                    setIsPageInputDisabled(
                                                      true
                                                    );

                                                    setInventoryImpactDialog({
                                                      pendingTransactions:
                                                        pendingTransactions,
                                                      handleConfirm: () => {
                                                        setInventoryTransactions(
                                                          (prev) => {
                                                            const transactionToUpdate =
                                                              findTransaction(
                                                                prev
                                                              );

                                                            if (
                                                              transactionToUpdate
                                                            ) {
                                                              for (const fieldName in fields) {
                                                                transactionToUpdate[
                                                                  fieldName
                                                                ] =
                                                                  fields[
                                                                    fieldName
                                                                  ];
                                                              }

                                                              updateInventoryTransaction(
                                                                transactionToUpdate
                                                              );
                                                            }

                                                            return [...prev];
                                                          }
                                                        );
                                                        setIsPageInputDisabled(
                                                          false
                                                        );
                                                      },
                                                      handleCancel: () => {
                                                        setIsPageInputDisabled(
                                                          false
                                                        );
                                                      },
                                                      onClose: () => {
                                                        setIsPageInputDisabled(
                                                          false
                                                        );
                                                      },
                                                    });
                                                  }
                                                };

                                              const poOptions = transactions
                                                .filter((t) =>
                                                  inboundOrderTypes.includes(
                                                    t.transactionType
                                                  )
                                                )
                                                .map((t) => ({
                                                  value:
                                                    t.transactionReferenceId,
                                                  label: `${
                                                    transactionTypeShortDescriptions[
                                                      t.transactionType
                                                    ]
                                                  }-${
                                                    t.transactionReferenceId
                                                  }: ${formatDateTime(
                                                    t.transactionOriginalRequiredAt,
                                                    dateFormats.short
                                                  )}`,
                                                }));

                                              const transactionTitle = `${
                                                transactionTypeDescriptions[
                                                  a.transactionType
                                                ]
                                              } ${
                                                a.transactionReferenceNumber
                                              }`;

                                              const isHighlighted =
                                                highlightOrderNumber ==
                                                a.transactionReferenceNumber;

                                              return (
                                                <React.Fragment key={ai}>
                                                  {a.allocatedQuantity + 1 ===
                                                    a.quantity &&
                                                  isCurrentStock &&
                                                  availablePartialQty > 0 ? (
                                                    <CompactTableRow>
                                                      <CompactTableCell
                                                        colSpan={11}
                                                      >
                                                        <Typography
                                                          color='error'
                                                          sx={{
                                                            fontWeight: 'bold',
                                                          }}
                                                        >
                                                          ↓ Only partial
                                                          quantity is available
                                                          to ship for this
                                                          order.
                                                        </Typography>
                                                      </CompactTableCell>
                                                    </CompactTableRow>
                                                  ) : null}
                                                  <CompactTableRow
                                                    sx={
                                                      isHighlighted
                                                        ? {
                                                            backgroundColor:
                                                              colors.rowHighlight,
                                                          }
                                                        : undefined
                                                    }
                                                  >
                                                    <CompactTableCell
                                                      sx={
                                                        (a.allocatedQuantity &&
                                                          a.allocatedQuantity !==
                                                            a.quantity) ||
                                                        (isTbd &&
                                                          a.unallocatedQuantity !==
                                                            a.quantity)
                                                          ? {
                                                              color:
                                                                colors.warningHigh,
                                                              fontWeight:
                                                                'bold',
                                                            }
                                                          : undefined
                                                      }
                                                    >
                                                      {(a.allocatedQuantity !==
                                                        undefined &&
                                                        printRoundedNumber(
                                                          a.allocatedQuantity,
                                                          0
                                                        )) ||
                                                        (isTbd &&
                                                          printRoundedNumber(
                                                            a.unallocatedQuantity,
                                                            0
                                                          ))}
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      {printRoundedNumber(
                                                        a.quantity,
                                                        0
                                                      )}
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      {transactionTypeDescriptions[
                                                        a.transactionType
                                                      ] || a.transactionType}
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      <Stack
                                                        direction='row'
                                                        spacing={1}
                                                      >
                                                        <Link
                                                          onClick={() => {
                                                            setSelectedTransaction(
                                                              {
                                                                ...a,
                                                                itemId:
                                                                  t.itemId,
                                                              }
                                                            );
                                                          }}
                                                        >
                                                          {
                                                            // Display asterisk for orders eating up component items.
                                                            `${a.transactionReferenceId}`.replaceAll(
                                                              ':0',
                                                              ' *'
                                                            )
                                                          }
                                                        </Link>
                                                        <Link
                                                          target='_blank'
                                                          href={`/order-entry?orderNo=${
                                                            a.transactionReferenceNumber ||
                                                            ''
                                                          }`}
                                                          title='View order in PAM order entry.'
                                                        >
                                                          <OpenInNewOutlined fontSize='small' />
                                                        </Link>
                                                      </Stack>
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      {formatDateTime(
                                                        a.transactionCreatedAt,
                                                        dateFormats.short
                                                      )}
                                                    </CompactTableCell>
                                                    <CompactTableCell
                                                      sx={{
                                                        color:
                                                          new Date(
                                                            a.transactionOriginalRequiredAt
                                                          ) < new Date() &&
                                                          colors.warningHigh,
                                                      }}
                                                    >
                                                      {formatDateTime(
                                                        a.transactionOriginalRequiredAt,
                                                        dateFormats.short
                                                      )}
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      <TransactionStatus
                                                        transaction={a}
                                                      />
                                                    </CompactTableCell>
                                                    <CompactTableCell
                                                      title={
                                                        a.customerId &&
                                                        `Account ID: ${a.customerId}`
                                                      }
                                                    >
                                                      {a.customerName || ''}
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      {a.salespersonName}
                                                    </CompactTableCell>
                                                    <CompactTableCell>
                                                      <GridContainer>
                                                        <GridItem xs={6}>
                                                          <LabeledInput
                                                            type='select-search'
                                                            disabled={
                                                              isPageInputDisabled
                                                            }
                                                            placeholder={
                                                              a.linkedTransactionReferenceId ||
                                                              'Select'
                                                            }
                                                            label=''
                                                            value={
                                                              a.linkedTransactionReferenceId
                                                            }
                                                            onChange={(
                                                              e,
                                                              reason
                                                            ) => {
                                                              if (
                                                                [
                                                                  'blur',
                                                                  'clear',
                                                                  'selectOption',
                                                                ].includes(
                                                                  reason
                                                                )
                                                              ) {
                                                                const newLinkedTransactionReferenceId =
                                                                  e.target
                                                                    .value ||
                                                                  '';

                                                                if (
                                                                  !newLinkedTransactionReferenceId
                                                                ) {
                                                                  openDialog({
                                                                    title:
                                                                      transactionTitle,
                                                                    message: `Unassign linked PO/Transfer?`,
                                                                    handleConfirm:
                                                                      () => {
                                                                        updateAllocatedTransaction(
                                                                          {
                                                                            linkedTransactionReferenceId:
                                                                              newLinkedTransactionReferenceId,
                                                                          }
                                                                        );
                                                                      },
                                                                  });
                                                                } else {
                                                                  updateAllocatedTransaction(
                                                                    {
                                                                      linkedTransactionReferenceId:
                                                                        newLinkedTransactionReferenceId,
                                                                    }
                                                                  );
                                                                }
                                                              }
                                                            }}
                                                            options={poOptions}
                                                          />
                                                        </GridItem>
                                                        <GridItem xs={6}>
                                                          <SmallCheckBox
                                                            label='Ship Complete'
                                                            checked={
                                                              a.isShipComplete
                                                            }
                                                            disabled={
                                                              isPageInputDisabled
                                                            }
                                                            onChange={(e) => {
                                                              const isChecked =
                                                                e.target
                                                                  .checked;
                                                              if (!isChecked) {
                                                                openDialog({
                                                                  title:
                                                                    transactionTitle,
                                                                  message: `Remove the ship complete flag?`,
                                                                  handleConfirm:
                                                                    () => {
                                                                      updateAllocatedTransaction(
                                                                        {
                                                                          isShipComplete:
                                                                            isChecked,
                                                                        }
                                                                      );
                                                                    },
                                                                });
                                                              } else {
                                                                updateAllocatedTransaction(
                                                                  {
                                                                    isShipComplete:
                                                                      isChecked,
                                                                  }
                                                                );
                                                              }
                                                            }}
                                                          />
                                                        </GridItem>
                                                      </GridContainer>
                                                    </CompactTableCell>
                                                  </CompactTableRow>
                                                </React.Fragment>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Card>
                                  </GridItem>
                                )}
                              </GridContainer>
                            </CardContainer>
                          </GridItem>
                        );
                      })}
                    </GridContainer>
                  </CardContainer>
                </GridItem>
              )
            )}
          </GridContainer>
        </GridItem>
      </GridContainer>
      <Dialog
        open={!!selectedTransaction.transactionReferenceNumber}
        onClose={() => setSelectedTransaction({})}
        TransitionComponent={Grow}
        maxWidth='xl'
      >
        <IconButton
          aria-label='close'
          onClick={() => setSelectedTransaction({})}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <TransactionDetails transaction={selectedTransaction} />
        </DialogContent>
      </Dialog>
      <InventoryImpactDialog {...inventoryImpactDialog} />
    </ThemeProvider>
  );
};
